import React, { useEffect, useRef, useState } from 'react';
import './violation.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  getPaginationOpts,
  getResponseError, responseLog,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getViolationData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import ViolationForm from './ViolationForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import FilterForm from './FilterForm';
import { violationAdditionalColumns, violationColumns } from './violationColumns';
import {
  deleteViolation, loadViolationById, loadViolationByIdClear, loadViolationList,
} from '../actions';
import { AUTHORIZED_MODULE, VIOLATIONS_MODULE } from '../../../configs/moduleNames';
import { ROLE_TYPE } from '../../../configs';
import { getUserRole } from '../../../utils/routeByRoles';
import ViolationProtocolForm from './ViolationProtocolForm';

export default function ViolationPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showProtocolForm, setShowProtocolForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showReadOnly, setShowReadOnly] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    violationListMeta,
    loadingViolationList,
    violationList,
    loadingCurrentViolation,
    currentViolation,
    loadingCreateViolation,
    loadingEditViolationById,
    loadingDeleteViolation,
    loadingGenerateViolationProtocolPdf,
  } = useSelector(getViolationData);
  const { formatMessage } = useIntl();
  const rawAuthorizedData = useSelector(state => state[AUTHORIZED_MODULE]?.authorizedData || {});
  const userRole = getUserRole(rawAuthorizedData);
  const canManageViolations = userRole === ROLE_TYPE.ADMIN;

  const loadViolation = (id, canEdit = false) => {
    if (id) {
      setIsEdit(Boolean(canEdit));
      setShowReadOnly(!canEdit);
      dispatch(loadViolationById(id))
        .then(() => setShowForm(true))
        .catch(response => errorNotification('Загрузка нарушения', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[VIOLATIONS_MODULE].violationListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    // const p1 = dispatch(loadViolationList(requestData)).catch(responseLogWithNotification);
    const p1 = dispatch(loadViolationList(requestData)).catch(responseLog);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue, reset, watch,
  } = useFilterForm({}, loadData, ['violation_id']);
  const vid = watch('violation_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadViolation(vid, canManageViolations), [vid, canManageViolations]);

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.uuid === item.uuid) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    if (!canManageViolations) {
      return;
    }
    setIsEdit(false);
    setShowView(false);
    setShowReadOnly(false);
    clearCurrentState();
    setShowForm(true);
  };

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    if (!canManageViolations) {
      return;
    }
    loadViolation(item.uuid, true);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleView = item => event => {
    if (!item?.uuid) {
      errorNotification('Ошибка', 'Нет ID');
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    loadViolation(item.uuid, false);
  };

  const handleCreateProtocol = item => event => {
    if (!item?.uuid) {
      errorNotification('Ошибка', 'Нет ID');
      return;
    }
    if (!canManageViolations) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowForm(false);
    setShowProtocolForm(false);
    dispatch(loadViolationById(item.uuid))
      .then(() => setShowProtocolForm(true))
      .catch(response => errorNotification('Загрузка нарушения', getResponseError(response)));
  };

  const handleDelete = item => event => {
    if (!item.uuid) {
      errorNotification('Ошибка', 'Нет ID');
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление нарушения # ${item.uuid}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteViolation(item.uuid)).then(() => {
        if (selectedRow?.uuid === item.uuid) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление нарушения', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление нарушения', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    navigator.clipboard.writeText(`${window.location.href}?violation_id=${item.uuid}`);
    successNotification('Копирование ссылки на нарушение', 'Ссылка на просмотр скопирована.');
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('violation_id', '');
    clearCurrentState();
  };

  const handleProtocolClose = () => {
    setShowProtocolForm(false);
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadViolationByIdClear()).catch(responseLogWithNotification);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          {/* <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}> */}
          {/*  Копировать ссылку */}
          {/* </Button> */}
          <Button variant="link" className="w-100 more-button" onClick={handleView(item)}>
            Просмотреть
          </Button>
          {canManageViolations ? (
            <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
              Редактировать
            </Button>
          ) : null}
          {canManageViolations ? (
            <Button variant="link" className="w-100 more-button" onClick={handleCreateProtocol(item)}>
              Создать протокол
            </Button>
          ) : null}
          {canManageViolations ? (
            <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
              Удалить
            </Button>
          ) : null}
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.uuid === selectedRow.uuid) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingViolationList || loadingCurrentViolation || loadingDeleteViolation || loadingCreateViolation || loadingEditViolationById || loadingGenerateViolationProtocolPdf} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.violation' })}</div>
        <FilterRow>
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon width={24} height={24} icon="filter" />
          </Button>
          <div className="filter-sep" />
          {canManageViolations ? (
            <Button variant="second" onClick={handleCreate}>
              <Icons
                customIcon
                width={24}
                height={24}
                icon="plus"
              />
            </Button>
          ) : null}
          {isOpenFilter
            ? (
              <FilterForm
                control={control}
                onHide={() => {
                  setOpenFilter(false);
                }}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                setValue={setValue}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={(item, event) => (canManageViolations ? handleEdit(item)(event) : handleView(item)(event))}
                rowClassFormatter={rowClassFormatter}
                data={violationList}
                columns={violationColumns}
                additionalColumns={violationAdditionalColumns}
                name="violation-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={violationListMeta} list={violationList} handleChangeMeta={loadData} />
        </div>
      </div>
      {/* {showForm && vid ? ( */}
      {showForm ? (
        <ViolationForm
          currentViolation={currentViolation}
          isEdit={isEdit}
          isReadOnly={showReadOnly}
          loadData={loadData}
          onClose={handleClose}
          loadViolation={loadViolation}
        />
      ) : null}
      {showProtocolForm ? (
        <ViolationProtocolForm
          violation={currentViolation}
          loading={loadingGenerateViolationProtocolPdf}
          onClose={handleProtocolClose}
        />
      ) : null}
    </div>
  );
}
